import { useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

export function BrosClobberRestorePanel() {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);

  const runPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.brosClobberRestorePreview();
      setPreview(data);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setConfirmApply(false);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.brosClobberRestoreApply();
      setApplyResult(data);
      if (data.restored > 0) {
        toast.success(`Restored ${data.restored} BR OS invoice(s) to their confirmed state.`);
      } else {
        toast.info('Nothing to restore -- no clobber damage found.');
      }
      // Refresh preview so the card reflects the new state immediately.
      runPreview();
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card data-testid="bros-clobber-restore-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-blue-500" />
          BR OS Confirmed-Match Restore
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          BR OS&apos;s hourly resync used to unconditionally overwrite an invoice&apos;s
          matching_status/payment_status on every sync, silently reverting any confirmed
          match within an hour of confirmation (fixed in c2a82135 -- future syncs are now
          guarded). This repairs damage the bug already did before the fix landed, using
          paid_at&apos;s survival as proof a confirm genuinely happened. Preview is read-only.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={runPreview} disabled={loadingPreview} variant="outline" data-testid="bros-clobber-restore-preview-btn">
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={applying}
              data-testid="bros-clobber-restore-apply-btn"
            >
              Apply
            </Button>
          ) : (
            <Button variant="destructive" onClick={runApply} disabled={applying} data-testid="bros-clobber-restore-confirm-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Confirm Apply
            </Button>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
            Error: {error}
          </div>
        )}

        {preview && (
          <div className="space-y-2" data-testid="bros-clobber-restore-preview-results">
            <p className="text-sm font-medium">
              {preview.total === 0 ? (
                <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  No clobber damage found.
                </span>
              ) : (
                `${preview.total} invoice(s) would be restored`
              )}
            </p>
            {preview.candidates.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                      <th className="text-left px-3 py-2 font-medium">Entity</th>
                      <th className="text-left px-3 py-2 font-medium">Current</th>
                      <th className="text-left px-3 py-2 font-medium">Restore to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.candidates.slice(0, MAX_DISPLAY_ROWS).map((c) => (
                      <tr key={c.invoice_id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{c.invoice_number}</td>
                        <td className="px-3 py-2 text-xs">{c.entity_id}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">{c.current_matching_status}</Badge>{' '}
                          <Badge variant="outline" className="text-[10px]">{c.current_payment_status}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {c.restore_matching_status}
                          </Badge>{' '}
                          <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {c.restore_payment_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-1" data-testid="bros-clobber-restore-apply-results">
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              Restored {applyResult.restored}, skipped (raced) {applyResult.skipped_raced}, of{' '}
              {applyResult.total_candidates} candidate(s).
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
